import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen } from "@fortawesome/free-solid-svg-icons";
import useSubjectAPI from "./useSubjectAPI";
import { diet_types, stress_levels } from "../enums";
import { ENUM_MAPS, normalizeEnum } from "./enumMaps";

const LifestyleSection = ({ subjectId, form }) => {
  const isCreate = !subjectId;
  const [open, setOpen] = useState(true);

  const purple = "#2E2868";
  const pillBg = "#ebeaf3";

  const { loadSubject, saveSubjectDirect } = useSubjectAPI(subjectId);

  const [values, setValues] = useState({
    diet_type: "",
    stress_level: "",
    lifestyle_notes: "",
  });

  const [originalValues, setOriginalValues] = useState({});

  const [editing, setEditing] = useState({
    diet_type: false,
    stress_level: false,
    lifestyle_notes: false,
  });

  /* =========================
      LOAD (EDIT MODE ONLY)
   ========================= */
  useEffect(() => {
    if (isCreate) return;

    loadSubject().then((res) => {
      if (!res) return;
      setValues({
        diet_type: res.diet_type || "",
        stress_level: res.stress_level || "",
        lifestyle_notes: res.lifestyle_notes || "",
      });
    });
  }, [isCreate, subjectId]);

  useEffect(() => {
    if (!isCreate || !form?.setValue) return;

    form.setValue({
      key: "subjects",
      value: {
        ...(form.model?.subjects || {}),
        diet_type: values.diet_type || null,
        stress_level: values.stress_level || null,
        lifestyle_notes: values.lifestyle_notes || null,
      },
    });
  }, [values.diet_type, values.stress_level, values.lifestyle_notes]);

  const handleChange = (field, val) => {
    setValues((prev) => ({ ...prev, [field]: val }));
  };

  const toggleEdit = (field) => {
    if (isCreate) return;

    setOriginalValues((prev) => ({ ...prev, [field]: values[field] }));
    setEditing((prev) => ({ ...prev, [field]: true }));
  };

  const handleCancel = (field) => {
    setValues((prev) => ({ ...prev, [field]: originalValues[field] }));
    setEditing((prev) => ({ ...prev, [field]: false }));
  };

  const saveField = async (field) => {
    if (isCreate) return;

    setEditing((prev) => ({ ...prev, [field]: false }));

    const value = ENUM_MAPS[field]
      ? normalizeEnum(values[field], ENUM_MAPS[field])
      : values[field];

    const payload = {
      subjects: {
        id: subjectId,
        [field]: value,
      },
    };

    const res = await saveSubjectDirect(payload);
    if (!res) {
      alert("Save failed");

      handleCancel(field);
    }
  };

  const renderSelect = (label, field, options) => {
    const isEditing = editing[field];

    const viewStyle = {
      backgroundColor: pillBg,
      padding: "0.5rem 70px 0.5rem 0.5rem",
      cursor: "text",
      userSelect: "text",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    };

    const inputStyle = {
      backgroundColor: isEditing ? "#fff" : pillBg,
      border: isEditing ? `2px solid ${purple}` : "0",
      boxShadow: isEditing ? "0 0 5px rgba(46, 40, 104, 0.2)" : "none",
      padding: isEditing
        ? "0.5rem 110px 0.5rem 0.5rem"
        : "0.5rem 2rem 0.5rem 0.5rem",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      cursor: isCreate ? "pointer" : "text",
    };

    const selectedOption = options.find((item) => item.id === values[field]);
    const displayValue = selectedOption
      ? selectedOption.label || selectedOption.id
      : "";

    return (
      <div className="mb-3">
        <label className="form-label mb-1">{label}</label>

        <div style={{ position: "relative" }}>
          {isCreate && (
            <select
              value={values[field]}
              onChange={(e) => handleChange(field, e.target.value)}
              className="form-select rounded-pill"
              style={inputStyle}
              title={displayValue}
            >
              <option value="">Select...</option>
              {options.map((item, idx) => (
                <option key={idx} value={item.id}>
                  {item.label || item.id}
                </option>
              ))}
            </select>
          )}

          {!isCreate && !editing[field] && (
            <>
              <div
                className="form-control rounded-pill border-0"
                style={viewStyle}
                title={displayValue}
              >
                {displayValue || "Select..."}
              </div>

              <FontAwesomeIcon
                icon={faPen}
                onClick={() => toggleEdit(field)}
                style={{
                  position: "absolute",
                  right: 14,
                  top: "50%",
                  transform: "translateY(-50%)",
                  cursor: "pointer",
                  color: purple,
                  fontSize: 14,
                  zIndex: 2,
                }}
              />
            </>
          )}

          {!isCreate && editing[field] && (
            <>
              <select
                value={values[field]}
                onChange={(e) => handleChange(field, e.target.value)}
                className="form-select rounded-pill"
                style={inputStyle}
              >
                <option value="">Select...</option>
                {options.map((item, idx) => (
                  <option key={idx} value={item.id}>
                    {item.label || item.id}
                  </option>
                ))}
              </select>

              <div
                style={{
                  position: "absolute",
                  right: 6,
                  top: "50%",
                  transform: "translateY(-50%)",
                  zIndex: 2,
                  display: "flex",
                  gap: "4px",
                }}
              >
                <button
                  onClick={() => saveField(field)}
                  className="btn btn-sm"
                  style={{
                    background: purple,
                    color: "#fff",
                    borderRadius: 8,
                    padding: "4px 10px",
                    fontSize: "11px",
                    cursor: "pointer",
                  }}
                >
                  Save
                </button>
                <button
                  onClick={() => handleCancel(field)}
                  className="btn btn-sm btn-light"
                  style={{
                    borderRadius: 8,
                    padding: "4px 10px",
                    fontSize: "11px",
                    cursor: "pointer",
                    border: "1px solid #ccc",
                  }}
                >
                  Cancel
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  const renderTextarea = (label, field) => {
    const isEditing = editing[field];

    const viewStyle = {
      backgroundColor: pillBg,
      padding: "0.5rem 70px 0.5rem 0.5rem",
      cursor: "text",
      userSelect: "text",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      minHeight: "38px",
      display: "flex",
      alignItems: "center",
    };

    const inputStyle = {
      backgroundColor: isEditing ? "#fff" : pillBg,
      border: isEditing ? `2px solid ${purple}` : "0",
      boxShadow: isEditing ? "0 0 5px rgba(46, 40, 104, 0.2)" : "none",
      padding: isEditing
        ? "0.5rem 110px 0.5rem 0.5rem"
        : "0.5rem 2rem 0.5rem 0.5rem",
      resize: "none",
      cursor: isCreate ? "pointer" : "text",
    };

    return (
      <div className="mb-3">
        <label className="form-label mb-1">{label}</label>

        <div style={{ position: "relative" }}>
          {isCreate && (
            <textarea
              rows={2}
              value={values[field]}
              onChange={(e) => handleChange(field, e.target.value)}
              className="form-control rounded-pill"
              style={inputStyle}
              title={values[field]}
            />
          )}

          {!isCreate && !editing[field] && (
            <>
              <div
                className="form-control rounded-pill border-0"
                style={viewStyle}
                title={values[field]}
              >
                {values[field]}
              </div>

              <FontAwesomeIcon
                icon={faPen}
                onClick={() => toggleEdit(field)}
                style={{
                  position: "absolute",
                  right: 14,
                  top: "50%",
                  transform: "translateY(-50%)",
                  cursor: "pointer",
                  color: purple,
                  fontSize: 14,
                  zIndex: 2,
                }}
              />
            </>
          )}

          {!isCreate && editing[field] && (
            <>
              <textarea
                rows={2}
                value={values[field]}
                onChange={(e) => handleChange(field, e.target.value)}
                className="form-control rounded-pill"
                style={inputStyle}
              />
              <div
                style={{
                  position: "absolute",
                  right: 6,
                  top: "50%",
                  transform: "translateY(-50%)",
                  zIndex: 2,
                  display: "flex",
                  gap: "4px",
                }}
              >
                <button
                  onClick={() => saveField(field)}
                  className="btn btn-sm"
                  style={{
                    background: purple,
                    color: "#fff",
                    borderRadius: 8,
                    padding: "4px 10px",
                    fontSize: "11px",
                    cursor: "pointer",
                  }}
                >
                  Save
                </button>
                <button
                  onClick={() => handleCancel(field)}
                  className="btn btn-sm btn-light"
                  style={{
                    borderRadius: 8,
                    padding: "4px 10px",
                    fontSize: "11px",
                    cursor: "pointer",
                    border: "1px solid #ccc",
                  }}
                >
                  Cancel
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="my-3" style={{ maxWidth: 490 }}>
      <div className="p-3">
        <div
          className="d-flex justify-content-between align-items-center mb-3"
          style={{ userSelect: "none" }}
        >
          <div className="d-flex align-items-center">
            <span
              style={{
                fontSize: "30px",
                color: "#b2a9ff",
                marginRight: "6px",
              }}
            >
              ⋮⋮
            </span>
            <h5
              className="fw-bold m-0"
              style={{ color: purple, cursor: "pointer" }}
              onClick={() => setOpen((prev) => !prev)}
            >
              Lifestyle
            </h5>
          </div>

          <div
            style={{
              width: "28px",
              height: "28px",
              borderRadius: "8px",
              backgroundColor: purple,
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              transition: "transform 0.3s ease",
              transform: open ? "rotate(90deg)" : "rotate(0deg)",
            }}
            onClick={() => setOpen((prev) => !prev)}
          >
            <i className="bi bi-chevron-right" style={{ fontSize: "14px" }} />
          </div>
        </div>

        {open && (
          <>
            {renderSelect("Diet Type", "diet_type", diet_types)}
            {renderSelect("Stress Level", "stress_level", stress_levels)}
            {renderTextarea("Lifestyle Notes", "lifestyle_notes")}
          </>
        )}
      </div>
    </div>
  );
};

export default LifestyleSection;
